import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  EQuickAdjustment,
  TArtifactLengthOptions,
  TReadingLevelOptions,
} from '@/hooks/promptCanvas/types';

import CustomQuickActionList from './CustomQuickActionList';
import DefaultQuickActions from './DefaultQuickActions';

type TProps = {
  quickAdjustmentType: EQuickAdjustment | undefined;
  setQuickAdjustmentType: Dispatch<
    SetStateAction<EQuickAdjustment | undefined>
  >;
  onSubmit: ({
    artifactLength,
    readingLevel,
    customAction,
  }: {
    artifactLength?: TArtifactLengthOptions;
    readingLevel?: TReadingLevelOptions;
    customAction?: string;
  }) => void;
  disabled?: boolean;
};

const QuickActions: FC<TProps> = ({
  quickAdjustmentType,
  setQuickAdjustmentType,
  onSubmit,
  disabled,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [sliderValue, setSliderValue] = useState(0);

  const quickActionRef = useRef<HTMLDivElement>(null);
  const customQuickActionButtonRef = useRef<HTMLDivElement>(null);
  const customQuickActionListRef = useRef<HTMLDivElement>(null);
  const customQuickActionPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isClickingSlider = !!sliderRef.current?.contains(target);
      const isClickingDefaultQuickActions =
        !!quickActionRef.current?.contains(target);
      const isClickingCustomQuickActions =
        !!customQuickActionButtonRef.current?.contains(target) ||
        !!customQuickActionListRef.current?.contains(target) ||
        !!customQuickActionPopoverRef.current?.contains(target);

      if (
        !(
          isClickingSlider ||
          isClickingDefaultQuickActions ||
          isClickingCustomQuickActions
        )
      ) {
        setQuickAdjustmentType(undefined);
        setSliderValue(0);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setQuickAdjustmentType]);

  const handleSubmitDefaultQuickAction = (
    value?: TArtifactLengthOptions | TReadingLevelOptions
  ) => {
    if (!quickAdjustmentType) return;

    const body =
      quickAdjustmentType === EQuickAdjustment.LENGTH
        ? {
            artifactLength: value as TArtifactLengthOptions,
          }
        : {
            readingLevel: value as TReadingLevelOptions,
          };

    onSubmit(body);
  };

  return (
    <div className="flex w-[85%] flex-col gap-1">
      <div className="flex flex-row gap-1">
        <div className="flex flex-row flex-wrap gap-1" ref={quickActionRef}>
          <DefaultQuickActions
            sliderRef={sliderRef}
            disabled={!!disabled}
            quickAdjustmentType={quickAdjustmentType}
            sliderValue={sliderValue}
            onSubmit={handleSubmitDefaultQuickAction}
            setQuickAdjustmentType={setQuickAdjustmentType}
            setSliderValue={setSliderValue}
          />
          <CustomQuickActionList
            onSubmit={onSubmit}
            quickAdjustmentType={quickAdjustmentType}
            setQuickAdjustmentType={setQuickAdjustmentType}
            disabled={!!disabled}
            customQuickActionButtonRef={customQuickActionButtonRef}
            customQuickActionListRef={customQuickActionListRef}
            customQuickActionPopoverRef={customQuickActionPopoverRef}
          />
        </div>
      </div>
    </div>
  );
};

export default QuickActions;
